import { useMemo } from "react";
import { motion } from "framer-motion";
import { Check, Circle, Lightbulb } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  calculateGeoQualityScore, 
  PartialPromptData,
  getQualityLevelColor,
  getQualityLevelLabel
} from "@/lib/geo-quality-score";
import { GeoIntent, GeoUseCase } from "@/lib/types";
import { cn } from "@/lib/utils";

interface GeoQualityProgressProps {
  promptData: {
    title: string;
    description: string;
    content: string;
    category: string;
  };
  geoData: {
    intent: GeoIntent | "";
    useCase: GeoUseCase | "";
    recommendedModel: string;
    testedByAuthor: boolean;
  };
}

export function GeoQualityProgress({ promptData, geoData }: GeoQualityProgressProps) {
  const qualityScore = useMemo(() => {
    const partialPrompt: PartialPromptData = {
      title: promptData.title,
      description: promptData.description,
      content: promptData.content,
      category: promptData.category,
      geoMetadata: {
        intent: geoData.intent || undefined,
        use_case: geoData.useCase || undefined,
        models: geoData.recommendedModel ? { recommended: geoData.recommendedModel } : undefined,
        reliability: {
          tested_by_author: geoData.testedByAuthor,
          community_verified: 0,
        },
      },
    };
    return calculateGeoQualityScore(partialPrompt);
  }, [promptData, geoData]);

  // Calculate stroke dasharray for circular progress
  const circumference = 2 * Math.PI * 40; // radius = 40
  const strokeDashoffset = circumference - (qualityScore.score / 100) * circumference;

  return (
    <Card className="border-border/50 bg-card/50 sticky top-4">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-brand-cyan" />
          Quality Score
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Circular Progress */}
        <div className="flex justify-center">
          <div className="relative w-24 h-24">
            <svg className="w-24 h-24 transform -rotate-90">
              {/* Background circle */}
              <circle
                cx="48"
                cy="48"
                r="40"
                stroke="currentColor"
                strokeWidth="6"
                fill="none"
                className="text-muted/30"
              />
              {/* Progress circle */}
              <motion.circle
                cx="48"
                cy="48"
                r="40"
                stroke="currentColor"
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                className={cn(
                  qualityScore.level === 'verified' && "text-brand-cyan",
                  qualityScore.level === 'excellent' && "text-purple-500",
                  qualityScore.level === 'good' && "text-blue-500",
                  qualityScore.level === 'basic' && "text-muted-foreground"
                )}
                initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
                animate={{ strokeDashoffset }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </svg>
            {/* Center text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn(
                "text-xl font-bold",
                getQualityLevelColor(qualityScore.level)
              )}>
                {qualityScore.score}%
              </span>
            </div>
          </div>
        </div>

        {/* Level Label */}
        <div className="text-center">
          <span className={cn(
            "text-sm font-medium",
            getQualityLevelColor(qualityScore.level)
          )}>
            {getQualityLevelLabel(qualityScore.level)}
          </span>
        </div>

        {/* Checklist */}
        <div className="space-y-2">
          {qualityScore.breakdown.map((item) => (
            <div 
              key={item.field}
              className="flex items-center gap-2 text-xs"
            >
              {item.completed ? (
                <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
              ) : (
                <Circle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              )}
              <span className={cn(
                item.completed ? "text-foreground" : "text-muted-foreground"
              )}>
                {item.field}
              </span>
            </div>
          ))}
        </div>

        {/* Top Suggestion */}
        {qualityScore.suggestions.length > 0 && (
          <div className="pt-2 border-t border-border/50">
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <Lightbulb className="h-3 w-3 mt-0.5 text-yellow-500 shrink-0" />
              <span>{qualityScore.suggestions[0]}</span>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
